import { useAnimations } from "@react-three/drei";
import { useSafeGLTF } from "../../../utils/safeGLTFLoader";
import { GroupProps, useThree } from "@react-three/fiber";
import { Color, LoopPingPong, MeshPhysicalMaterial, Object3D, Mesh, Vector3, Group } from "three";
import { FunctionComponent, Ref, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { HALF_PI, REGEXP_BRACKET, REGEXP_CABINET, REGEXP_POLKA_STOL } from "../../../constants";
import { generateID } from "../../../store/helpers/generateID";
// @ts-ignore
import { clone } from "three/examples/jsm/utils/SkeletonUtils.js";
import { useCurrent } from "../../../hooks/useCurrent";
import { BracketsItem, ItemI, PartInterface, SectionItemI } from "../../../shared";
import { useColors } from "../../../hooks/useColors";
import { Draggable } from "./Draggable";
import { useStoreSystem } from "../../../store";
import { SceneContext } from "../../Context/SceneContext";
import { useShelvingStore } from "../../../store/useShelvingStore";
import shallow from "zustand/shallow";
import { useHDR } from "../../../hooks";
import { changeAllChildrenMaterial } from "../../../store/actions/items/changeAllChildrenColor";
import { SideTypeWithoutTop } from "../../../store/types/store";
import { getShelfRodChainIds } from "../../../store/helpers/updateShelfRodIds";

export type ShelfProps = GroupProps & {
    model: string;
    itemId: string;
    sectionId: string;
    newSectionId: string;
    brackets: BracketsItem;
    parts: PartInterface[];
    position: number[];
    partPosition: number[];
    leftRacksStart: number[];
    bracketsPosition: {
        left: number[];
        right: number[];
    };
    shelfName: string;
    isSpirit?: boolean;
    shelf: SectionItemI;
    startXPosition: number;
    side: SideTypeWithoutTop;
};

function getItemShift(item: SectionItemI) {
    const isType = (t: string) => Array.isArray(item.type) && item.type?.includes(t);

    if (isType("shelf")) {
        const x = 0.0;
        const y = 0.011;
        const z = 0.0025;

        return {
            item: [x, y - 0.001, z],
            left: [x - 0.002, y, z],
            right: [x + 0.002, y, z],
            parts: item.parts.map(() => [0, 0, 0]),
        };
    }

    // Проверяем перфопанели по типу или по названию (для рейлингов-перфопанелей)
    const isPerforatedPanel = isType("punchPanel") || isType("Перфопанель");

    const isRailing = isType("Рейлинг");

    if (isPerforatedPanel) {
        const x = 0.0;
        const y = -0.0045;
        const z = +0.018; // Положительное значение, так как вычитается из position

        return {
            item: [x, y - 0.001, z],
            left: [x, y, z],
            right: [x, y, z],
            parts: item.parts.map(() => [0, 0, 0]),
        };
    }

    if (isRailing) {
        const x = 0.0;
        const y = -0.0045;
        const z = -0.007;

        return {
            item: [x, y - 0.001, z],
            left: [x, y, z],
            right: [x, y, z],
            parts: item.parts.map(() => [0, 0, 0]),
        };
    }

    if (isType("railing")) {
        const x = -0.003;
        const y = -0.0075;
        const z = -0.21;

        return {
            item: [x, y - 0.001, z],
            left: [x, y, z],
            right: [x, y, z],
            parts: item.parts.map(() => [0, 0, 0]),
        };
    }

    if (isType("locker")) {
        const x = -0.0;
        const y = -0.0225;
        const z = -0.004;

        return {
            item: [x, y, z],
            left: [x, y, z],
            right: [x, y, z],
            parts: item.parts.map(() => [0, 0, 0]),
        };
    }

    if (isType("workbench")) {
        const x = -0.0;
        const y = -0.003;
        const z = -0.0035;

        return {
            item: [x, y, z],
            left: [x, y, z],
            right: [x, y, z],
            parts: item.parts.map(() => [0, 0, 0]),
        };
    }

    if (isType("Модуль (держатель)")) {
        const x = 0;
        const y = item.width === 600 ? -0.02 : -0.07;
        const z = 0;

        return {
            item: [x, y, z],
            left: [x, y, z],
            right: [x, y, z],
            parts: item.parts.map((p, index) => [0, index * -0.03, index * -0.25]),
        };
    }

    if (isType("Подвес")) {
        const x = 0;
        const y = 0;
        const z = 0;

        return {
            item: [x, y, z - 0.13],
            left: [x, y - 0.02, z],
            right: [x, y - 0.02, z],
            parts: item.parts.map((p, index) => [index === 0 ? -0.11 : index * 0.13, 0.05, -0.14]),
        };
    }

    if (isType("Штанга")) {
        const x = 0;
        const y = -0.01;
        const z = 0;

        return {
            item: [x, y, z],
            left: [x, y, z],
            right: [x, y, z],
            parts: item.parts.map(() => [0, 0, 0]),
        };
    }

    return {
        item: [0, 0, 0],
        left: [0, 0, 0],
        right: [0, 0, 0],
        parts: item.parts.map((p) => {
            if (p.type?.some((t) => t === "Штанга")) {
                // Для всех полок ставим штангу на глубине 160мм от переднего края
                // Рассчитываем смещение от центра полки (160мм - половина_глубины) / 1000

                // const centerOffset = (targetDepth -) / 1000; // смещение от центра в метрах
                const zOffset = item.depth / 2000;

                return [0, -0.003, zOffset - 0.26];
            }
            return [0, 0, 0];
        }),
    };
}

function getItemDeltaRotation(item: SectionItemI) {
    const isType = (t: string) => Array.isArray(item.type) && item.type?.includes(t);

    if (isType("Модуль (держатель)")) {
        const x = 0;
        const y = -HALF_PI;
        const z = 0;

        return {
            item: [x, y, z],
            left: [x, y, z],
            right: [x, y, z],
            parts: item.parts.map((part) => {
                if (part.name.toLowerCase().includes("колесо")) {
                    return [0, -HALF_PI, HALF_PI / 10];
                }
                return [0, 0, 0];
            }),
        };
    }

    if (isType("Подвес")) {
        const x = 0;
        const y = 0;
        const z = 0;

        return {
            item: [x, y, z],
            left: [x, y, z],
            right: [x, y, z],
            parts: item.parts.map((part) => {
                if (part.name.toLowerCase().includes("колесо")) {
                    return [0, 0, 0];
                }
                return [0, 0, 0];
            }),
        };
    }

    return {
        item: [0, 0, 0],
        left: [0, 0, 0],
        right: [0, 0, 0],
        parts: item.parts.map(() => [0, -HALF_PI, 0]),
    };
}

export const Shelves: FunctionComponent<ShelfProps> = ({
    model,
    itemId,
    newSectionId,
    sectionId,
    brackets,
    parts,
    bracketsPosition,
    partPosition,
    shelfName,
    position,
    isSpirit,
    leftRacksStart,
    shelf,
    startXPosition,
    side,
    ...props
}) => {
    // Безопасная загрузка всех GLTF моделей с защитой от ошибок
    const { scene, animations } = useSafeGLTF(model);
    const { scene: threeScene } = useThree();
    const isType = (t: string) => Array.isArray(shelf.type) && shelf.type?.includes(t);

    const gltfLeftBrackets = useSafeGLTF(
        brackets.left.elements.map((element) => element.model) || `${process.env.PUBLIC_URL}/models/connector.glb`
    );
    const gltfRightBrackets = useSafeGLTF(
        brackets.right.elements.map((element) => element.model) || `${process.env.PUBLIC_URL}/models/connector.glb`
    );
    const gltfParts = useSafeGLTF(parts.map((part) => part.model) || `${process.env.PUBLIC_URL}/models/connector.glb`);
    const { hdrTexture } = useHDR();
    const [isDrag, setIsDrag] = useState(false);
    const { setSectionItemsYPos, updateBrackets, updateItem, removeSpiritItems } = useShelvingStore(
        (state) => ({
            addSpiritItems: state.addSpiritItems,
            setSectionItemsYPos: state.setSectionItemsYPos,
            updateBrackets: state.updateBrackets,
            updateItem: state.updateItem,
            removeSpiritItems: state.removeSpiritItems,
            getCurrentShelving: state.getCurrentShelving,
            removeItem: state.removeItem,
        }),
        shallow
    );

    const { currentSide, setAnimationAction, isVisibleSizes, itemsGroup } = useStoreSystem(
        (state) => ({
            setAnimationAction: state.setAnimationAction,
            isVisibleSizes: state.settings.isVisibleSizes,
            currentSide: state.currentSide,
            itemsGroup: state.itemsGroup,
        }),
        shallow
    );

    const { setCurrentEditItem, setCurrentSectionId, currentEditItem } = useCurrent();
    const { colorsShade, colorsItems } = useColors();

    const activeObject = useContext(SceneContext);
    const refShelf = useRef<Object3D | Group | null>(null);
    const primitiveRef = useRef(null);

    const [isOpen, setIsOpen] = useState<boolean>(true);
    const dragStartPositionYShelf = useRef<number>();
    const activeSectionId = useRef<string>(newSectionId);

    const partWithWheel = useMemo(() => {
        return shelf.parts.find((part) => part.name.toLowerCase().includes("колесо"));
    }, []);

    const changeObjMaterial = useCallback((object: Object3D, mainColor: Color, secondColor = mainColor) => {
        const changeColors = (materialName: string, material: MeshPhysicalMaterial, color: Color) => {
            if (materialName) {
                material.transparent = false;
                material.color = color;
                material.opacity = 1;
                material.roughness = 0.8;
                material.metalness = 0.5;
            }
        };
        if (object.children.length > 0) {
            object.children.forEach((mesh: any, index: number) => {
                if (mesh instanceof Mesh) {
                    // @ts-ignore
                    const material = object.children[index].material;
                    const materialName = material?.name;
                    const color =
                        object.children.length > 1 && index === object.children.length - 1 ? secondColor : mainColor;
                    changeColors(materialName, material, color);
                } else if (mesh instanceof Object3D) {
                    mesh.traverse((object: Object3D) => {
                        // @ts-ignore
                        const material = object.material;
                        const materialName = material?.name;
                        changeColors(materialName, material, mainColor);
                    });
                }
            });
        }
    }, []);

    useEffect(() => {
        if (primitiveRef?.current && isSpirit) {
            changeAllChildrenMaterial(primitiveRef.current, 0x008d24, {
                transparent: true,
                opacity: 0.2,
                roughness: 0.2,
                metalness: 0,
            });
        }
    }, [isSpirit, primitiveRef]);
    const assignShelfMaterials = (shelf: Object3D) => {
        shelf.traverse((object: Object3D) => {
            if (shelfName.match(REGEXP_POLKA_STOL)) {
                changeObjMaterial(object, colorsShade[colorsItems.shelves]);
            } else if (shelfName.match(REGEXP_CABINET)) {
                changeObjMaterial(object, colorsShade[colorsItems.shelves], colorsShade[colorsItems.brackets]);
            } else if (shelfName.match(REGEXP_BRACKET)) {
                changeObjMaterial(object, colorsShade[colorsItems.brackets]);
            } else if (isType("Штанга")) {
                // Для родов используем цвет кронштейнов
                changeObjMaterial(object, colorsShade[colorsItems.brackets]);
            } else {
                changeObjMaterial(object, colorsShade[colorsItems.perfpanels]);
            }
        });
    };

    const objectShelf = useMemo(() => {
        const shelf = clone(scene);
        assignShelfMaterials(shelf);

        return shelf;
    }, [scene, colorsItems, shelfName, isSpirit, itemId]);

    const objectLeftBracket = useMemo(() => {
        return gltfLeftBrackets.map((bracket) => {
            const leftObjectBracket = clone(bracket.scene);
            leftObjectBracket.traverse((object: Object3D) => {
                changeObjMaterial(object, colorsShade[colorsItems.brackets]);
            });
            return leftObjectBracket;
        });
    }, [gltfLeftBrackets, colorsItems.brackets]);

    const objectRightBracket = useMemo(() => {
        return gltfRightBrackets.map((bracket) => {
            const rightObjectBracket = clone(bracket.scene);
            rightObjectBracket.traverse((object: Object3D) => {
                changeObjMaterial(object, colorsShade[colorsItems.brackets]);
            });
            return rightObjectBracket;
        });
    }, [gltfRightBrackets, colorsItems.brackets]);

    const objectParts = useMemo(() => {
        if (partWithWheel && isVisibleSizes) {
            return [];
        }

        const returnObjectParts = gltfParts.map((part, index) => {
            const objectPart = part.scene.clone();
            if (parts[index] && parts[index].name.toLowerCase().includes("колесо")) {
                if (hdrTexture) {
                    Object.entries(part.materials).forEach((material) => {
                        material[1].envMap = hdrTexture;
                    });
                }
                return objectPart;
            }

            // Проверяем, является ли эта часть штангой и продолжением
            const isRodPart = parts[index] && parts[index].type?.some((t) => t === "Штанга");
            if (isRodPart) {
                const currentShelving = useShelvingStore.getState().getCurrentShelving();
                if (currentShelving && currentShelving.sections) {
                    // Для частей штанг используем updateShelfRodIds логику
                    const rodChainInfo = getShelfRodChainIds(currentShelving.sections);
                    const currentRodInfo = rodChainInfo.find(
                        (info) => info.itemId === itemId && info.partIndex === index
                    );

                    if (currentRodInfo) {
                        // Для штанги-части-продолжения устанавливаем shapeKey "continue" = 1
                        objectPart.traverse((child) => {
                            if (child instanceof Mesh && child.morphTargetInfluences) {
                                const morphTargetDict = child.morphTargetDictionary;
                                if (morphTargetDict && "continue" in morphTargetDict) {
                                    const continueIndex = morphTargetDict["continue"];
                                    child.morphTargetInfluences[continueIndex] = currentRodInfo.shouldUseAltId ? 1 : 0;
                                    console.log(
                                        `Штанга-часть ${itemId} (part ${index}): установлен shapeKey 'continue' = 1`
                                    );
                                }
                            }
                        });
                    }
                }
            }

            objectPart.traverse((object: Object3D) => {
                changeObjMaterial(object, colorsShade[colorsItems.brackets]);
            });
            return objectPart;
        });

        return returnObjectParts;
    }, [gltfParts, colorsItems.brackets, isVisibleSizes, parts, itemId]);

    let animationsModel = useAnimations(animations, primitiveRef);

    const onClickShelfHandler = (open = isOpen) => {
        const closeAction = animationsModel?.actions["close"];
        const openAction = animationsModel?.actions["open"];
        if (!openAction || !closeAction) {
            return;
        }
        if (open) {
            if (openAction.isRunning()) {
                return;
            }
            openAction.stop();
            closeAction.setLoop(LoopPingPong, Infinity);
            setTimeout(() => closeAction.halt(0), closeAction.blendMode);
            closeAction.play();
            setIsOpen(false);
            setAnimationAction(() => onClickShelfHandler(false));
        } else {
            if (closeAction.isRunning()) {
                return;
            }
            closeAction.stop();
            openAction.setLoop(LoopPingPong, Infinity);
            setTimeout(() => openAction.halt(0), openAction.blendMode);
            openAction.play();
            setIsOpen(true);
            setAnimationAction(() => onClickShelfHandler(true));
        }
    };

    const shift = getItemShift(shelf);
    const deltaRotation = getItemDeltaRotation(shelf);

    const onEdit = () => {
        if (currentEditItem.id) {
            if (activeObject) {
                activeObject.current = null;
            }
        }
        removeSpiritItems(itemId);
        if (primitiveRef.current) {
            assignShelfMaterials(primitiveRef.current);
        }
        setCurrentEditItem(itemId, false, undefined, position as Vector3);
        setCurrentSectionId(sectionId);
        if (activeObject) {
            activeObject.current = refShelf.current;
        }
        updateBrackets(itemId, sectionId);
    };

    // Отдельная функция для выделения родов без зеленой подсветки
    const onEditRod = () => {
        if (currentEditItem.id) {
            if (activeObject) {
                activeObject.current = null;
            }
        }
        removeSpiritItems(itemId);
        if (primitiveRef.current) {
            assignShelfMaterials(primitiveRef.current);
        }
        setCurrentEditItem(itemId, false, undefined, position as Vector3);
        setCurrentSectionId(sectionId);
        if (activeObject) {
            activeObject.current = refShelf.current;
        }
        updateBrackets(itemId, sectionId);
    };

    const onDragStart = () => {
        dragStartPositionYShelf.current = position[1];
        if (isSpirit) {
            removeSpiritItems(itemId);
            return;
        } else {
            removeSpiritItems();
        }
        setCurrentSectionId(sectionId);

        setAnimationAction(animations.length !== 0 ? onClickShelfHandler : null);

        if (primitiveRef.current) {
            // делаем его зеленым призраком
            changeAllChildrenMaterial(primitiveRef.current, 0x008d24, {
                transparent: true,
                opacity: 0.2,
                roughness: 0.2,
                metalness: 0,
            });
        }

        setCurrentEditItem(itemId, true);
        setSectionItemsYPos(itemId, sectionId);
    };

    const onPointerOutShelf = useCallback(() => {
        if (currentSide == side) {
            document.body.style.cursor = "default";
        }
    }, [side, currentSide]);

    const onPointerEnterShelf = useCallback(() => {
        if (currentSide == side) {
            document.body.style.cursor = "pointer";
        }
    }, [side, currentSide]);

    const onDrag = (onDragStartSectionId: string, newSectionId: string) => {
        setIsDrag(true);
        if (onDragStartSectionId !== newSectionId && Boolean(newSectionId)) {
            activeSectionId.current = newSectionId;
        }
        // console.log({itemId})
        // console.log({newSectionId})

        setSectionItemsYPos(itemId, newSectionId);
    };

    const onDragEnd = () => {
        setIsDrag(false);
        if (currentEditItem.id) {
            setCurrentSectionId(undefined);

            if (primitiveRef.current) {
                assignShelfMaterials(primitiveRef.current);
            }

            // Восстанавливаем нормальный цвет родов после окончания перетаскивания полки
            if (shelf.type?.includes("Полка")) {
                const currentShelving = useShelvingStore.getState().getCurrentShelving();

                currentShelving.sections.forEach((section) => {
                    const currentShelf = section.items.find((i) => i.id === shelf.id);
                    if (!currentShelf) return;
                    const currentShelfHeight = currentShelf.position[1];
                    const currentShelfPositionX = currentShelf.position[0];
                    const currentShelfSectionId = currentShelf.sectionID;
                    section.items.forEach((item) => {
                        if (
                            item.type?.includes("Штанга") &&
                            Math.abs(item.position[1] - currentShelfHeight) < 0.01 &&
                            Math.abs(item.position[0] - currentShelfPositionX) < 0.01 &&
                            item.sectionID === currentShelfSectionId
                        ) {
                            // Находим 3D объект рода в сцене
                            const rodGroup = threeScene.getObjectByName(item.id) as Object3D;
                            if (rodGroup) {
                                const rodShelf = rodGroup.getObjectByName("shelf") as Object3D;
                                if (rodShelf) {
                                    // Восстанавливаем нормальный цвет рода используя правильную логику
                                    rodShelf.traverse((object: Object3D) => {
                                        // Для родов используем цвет кронштейнов
                                        changeObjMaterial(object, colorsShade[colorsItems.brackets]);
                                    });
                                }
                            }
                        }
                    });
                });
            }

            if (sectionId === activeSectionId.current) {
                updateBrackets(itemId, activeSectionId.current);
                updateItem(itemId, sectionId);
                updateBrackets(itemId, activeSectionId.current);
            } else {
                updateItem(itemId, sectionId);
                updateBrackets(itemId, sectionId, dragStartPositionYShelf.current);
                updateBrackets(itemId, activeSectionId.current);
            }
            setCurrentEditItem(undefined, false);
        }
    };

    return (
        <Draggable
            itemId={itemId}
            sectionId={sectionId}
            side={side}
            shelf={shelf}
            position={position}
            leftRacksStart={leftRacksStart}
            shift={shift.item}
            startXPosition={startXPosition}
            onEdit={isType("Штанга") ? onEditRod : onEdit}
            onDragStart={onDragStart}
            onDrag={onDrag}
            onDragEnd={onDragEnd}
        >
            <group ref={refShelf as Ref<Group>} name={itemId}>
                <group
                    {...props}
                    name={"shelf"}
                    position={[position[0] - shift.item[0], position[1] - shift.item[1], position[2] - shift.item[2]]}
                    rotation={[0, -HALF_PI, 0]}
                    onPointerOut={onPointerOutShelf}
                    onPointerEnter={onPointerEnterShelf}
                >
                    <primitive ref={primitiveRef} object={objectShelf} />

                    {/* {isSpirit && (
                        <mesh position={[0, 0, 0]}>
                            <sphereGeometry args={[0.05, 8, 6]} />
                            <meshBasicMaterial color="red" />
                        </mesh>
                    )} */}
                </group>
                {!isDrag &&
                    brackets.left.isVisible &&
                    brackets.left &&
                    position &&
                    objectLeftBracket.map((bracket, index) => {
                        // console.log('DEBUG SHELF: Creating left bracket', index, 'at position:', [
                        //     bracketsPosition.left[0] - shift.left[0],
                        //     bracketsPosition.left[1] - shift.left[1],
                        //     bracketsPosition.left[2] - shift.left[2],
                        // ]);
                        return (
                            <primitive
                                key={generateID()}
                                name={`leftBracket_${index}`}
                                rotation={[0, -HALF_PI, 0]}
                                position={[
                                    bracketsPosition.left[0] - shift.left[0],
                                    bracketsPosition.left[1] - shift.left[1],
                                    bracketsPosition.left[2] - shift.left[2],
                                ]}
                                object={bracket}
                            />
                        );
                    })}
                {!isDrag &&
                    brackets.right.isVisible &&
                    brackets.right &&
                    objectRightBracket.map((bracket, index) => {
                        // console.log('DEBUG SHELF: Creating right bracket', index, 'at position:', [
                        //     bracketsPosition.right[0] - shift.right[0],
                        //     bracketsPosition.right[1] - shift.right[1],
                        //     bracketsPosition.right[2] - shift.right[2],
                        // ]);
                        return (
                            <primitive
                                key={generateID()}
                                name={`rightBracket_${index}`}
                                rotation={[0, -HALF_PI, 0]}
                                position={[
                                    bracketsPosition.right[0] - shift.right[0],
                                    bracketsPosition.right[1] - shift.right[1],
                                    bracketsPosition.right[2] - shift.right[2],
                                ]}
                                object={bracket}
                            />
                        );
                    })}
                {!isDrag &&
                    objectParts.map((part, index) => {
                        // Safety check: ensure shift.parts[index] exists
                        const partShift = shift.parts[index] || [0, 0, 0];
                        const partRotation = deltaRotation.parts[index] || [0, 0, 0];

                        return (
                            <primitive
                                key={generateID()}
                                rotation={partRotation}
                                position={[
                                    partPosition[0] - partShift[0],
                                    partPosition[1] - partShift[1],
                                    partPosition[2] - partShift[2],
                                ]}
                                object={part}
                            />
                        );
                    })}
                {/*{parts.some(part => part.name.toLowerCase().includes('колесо')) &&*/}
                {/*    objectParts.map((part, index) => (*/}
                {/*        <primitive key={generateID()}*/}
                {/*                   rotation={deltaRotation.parts[0]}*/}
                {/*                   position={[*/}
                {/*                       partPosition[0] - shift.parts[index][0],*/}
                {/*                       partPosition[1] - shift.parts[index][1] + 0.034,*/}
                {/*                       partPosition[2] - shift.parts[index][2] + 0.25,*/}
                {/*                   ]} object={part.clone()}/>*/}
                {/*    ))*/}
                {/*}*/}
            </group>
        </Draggable>
    );
};
